import {
  ChatMessage,
  ChatProvider,
  ChatSession,
  ChatbotProviderProps,
  ChatProviderRef,
} from "@altimateai/ui-components/chatbotV2";
import { API_URL } from "@/config";
import { useMemo, forwardRef, useRef, useCallback, useImperativeHandle } from "react";
import { IconBook, IconBook2 } from "@tabler/icons-react";
import useAppContext, { useAppState } from "@/modules/app/useAppContext";
import useAppActions from "@/modules/app/useAppActions";
import { useQuery } from "@tanstack/react-query";
import { getKnowledgeBaseSources } from "@/helpers/apis";
import PromptLibraryDrawer, {
  PromptLibraryDrawerRef,
} from "@/pages/playground/promptLibrary/PromptLibraryDrawer";
import ImprovePromptButton from "@/components/chatbot/ImprovePromptButton";
import { DatamatesSelector } from "@/pages/playground/DatamatesSelector";

export interface PlaygroundChatbotProps {
  enableImprovePrompt?: boolean;
  chatProviderProps?: Partial<ChatbotProviderProps>;
}

export interface PlaygroundChatbotRef {
  resetSession: () => void;
  resetStatus: () => void;
  openPromptLibrary: () => void;
  getSessionById: (sessionId: string) => ChatSession | null;
}

/**
 * Common chatbot component for playgroung page and global playground popover
 */
const PlaygroundChatbot = forwardRef<PlaygroundChatbotRef, PlaygroundChatbotProps>(
  ({ enableImprovePrompt = false, chatProviderProps = {} }, ref) => {
    const { dispatch } = useAppContext();
    const { getCommonHeaders } = useAppActions(dispatch);
    const { enablePromptLibrary } = useAppState();
    const { loggedinUser, isDatabricksTenant } = useAppState();
    // AI-6339: provider drives per-provider citation URL routing in Markdown.
    const provider: "snowflake" | "databricks" = isDatabricksTenant ? "databricks" : "snowflake";

    // Memoize headers to prevent creating new object on every render
    const headers = useMemo(() => getCommonHeaders(), [getCommonHeaders]);

    const chatProviderRef = useRef<ChatProviderRef>(null);
    const promptLibraryDrawerRef = useRef<PromptLibraryDrawerRef>(null);

    // Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        resetSession: () => chatProviderRef.current?.resetSession(),
        resetStatus: () => chatProviderRef.current?.resetStatus(),
        openPromptLibrary: () => promptLibraryDrawerRef.current?.open(),
        getSessionById: (sessionId: string) =>
          chatProviderRef.current?.getSessionById(sessionId) ?? null,
      }),
      []
    );

    const { data: knowledgeSources } = useQuery({
      queryKey: ["knowledgeSources"],
      queryFn: () => getKnowledgeBaseSources(),
      enabled: !!loggedinUser, // Only run when authenticated
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes (no refetch on remount)
      gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    });

    const handleSavePrompt = useCallback(
      (message: ChatMessage) => {
        if (enablePromptLibrary && message.content && message.role === "user") {
          promptLibraryDrawerRef.current?.openWithPromptText(message.content);
        }
      },
      [enablePromptLibrary]
    );

    const knowledgeOptions = useMemo(() => {
      return (
        knowledgeSources?.map(source => ({
          id: source.id.toString(),
          label: source.name,
        })) || []
      );
    }, [knowledgeSources]);

    const handleOpenPromptLibrary = () => {
      promptLibraryDrawerRef.current?.open();
    };

    // Build components conditionally
    const components = useMemo(() => {
      const result: Partial<ChatbotProviderProps["components"]> = {};
      if (enableImprovePrompt) {
        result.questionFormButtons = (userPrompt: string, onAccept: (text: string) => void) => (
          <ImprovePromptButton userPrompt={userPrompt} onAccept={onAccept} />
        );
      }
      return result;
    }, [enableImprovePrompt]);

    // Build questionFormLeftActions conditionally
    const questionFormLeftActions = useMemo(() => {
      const actions = [];
      actions.push(<DatamatesSelector key="datamates-selector" />);
      return actions;
    }, []);

    const defaultProps: Partial<ChatbotProviderProps> = useMemo(
      () => ({
        assistantMeta: {
          name: "Studio",
          avatar: "🎮",
        },
        autoFocus: true,
        initialValidation: async () => {},
        provider,
        urls: {
          origin: API_URL,
          askPath: "/studio/ask",
          frontendUrl: window.location.origin,
          entityDetectionUrl: "",
          feedbackPath: "/feedbacks/studio",
        },
        requestParams: {
          headers,
        },
        fileUploadProps: {
          allowedFiles: ".pdf,.sql,.md,.txt,.csv,.json,.yaml,.yml",
          contextFieldKey: "uploaded_file",
        },
        classNames: {
          chatbot: "al-border-none !al-shadow-none al-bg-transparent",
          chatMessages: "al-flex al-flex-col al-pb-0 al-max-h-[100%]",
          messageItem: "[&>div]:al-max-w-full ",
        },
        contextOptions: [
          {
            label: "Knowledge Base",
            key: "knowledge",
            type: "submenu",
            options: knowledgeOptions,
            icon: <IconBook2 size={16} />,
          },
          ...(enablePromptLibrary
            ? [
                {
                  label: "View Prompt Library",
                  key: "knowledge",
                  type: "trigger" as const,
                  handler: handleOpenPromptLibrary,
                  icon: <IconBook size={16} />,
                },
              ]
            : []),
        ],
        components,
        questionFormLeftActions,
        handleSavePrompt,
        showSavePromptButton: enablePromptLibrary,
      }),
      [
        headers,
        knowledgeOptions,
        enablePromptLibrary,
        components,
        questionFormLeftActions,
        handleSavePrompt,
        provider,
      ]
    );

    // Merge default props with overrides from chatProviderProps
    // Memoize to prevent creating new object reference on every render
    const mergedProps = useMemo(
      () =>
        ({
          ...defaultProps,
          ...chatProviderProps,
          // Deep-merge `components` so consumer-supplied slots (e.g. messageActions)
          // compose with built-in slots (e.g. questionFormButtons) instead of
          // shallow-overwriting them.
          components: {
            ...defaultProps.components,
            ...chatProviderProps.components,
          },
        }) as ChatbotProviderProps,
      [defaultProps, chatProviderProps]
    );

    const handleUsePrompt = (text: string) => {
      // Close the prompt library drawer
      promptLibraryDrawerRef.current?.close();

      // Set the initial message state to trigger a re-render with the new message
      chatProviderRef.current?.setUserInput(text);
    };

    return (
      <>
        <ChatProvider ref={chatProviderRef} {...mergedProps} />
        {enablePromptLibrary && (
          <PromptLibraryDrawer ref={promptLibraryDrawerRef} onUsePrompt={handleUsePrompt} />
        )}
      </>
    );
  }
);

PlaygroundChatbot.displayName = "PlaygroundChatbot";

export default PlaygroundChatbot;
